import { useEffect, useRef, useState } from 'react';

/**
 * Слоган и слово владелицы — у каждой кнопки оформления ПРО.
 *
 * variant="full"   — слоган + круглое фото, фраза и подпись (витрины,
 *                    оплата, окна лимита);
 * variant="slogan" — только слоган (узкие места, где блоку с фото тесно).
 *
 * Слоган — ОДИН на страницу (правка владелицы 16.10.2026): из всех мест
 * вывода его рисует только первое по положению на странице, остальные —
 * без слогана (у variant="full" остаётся фото и слово владелицы). Ушло
 * первое место (сменили вкладку, страницу) — слоган переходит к следующему.
 * standalone — окно поверх страницы (лимит хорара, разовая покупка): там
 *        слоган у своей кнопки и в общий счёт страницы не входит.
 *
 * Тексты владелицы, правкам не подлежат. Цвета — токены темы (theme-*):
 * голубые на рф, сиреневые на ру. Фото 192×192 webp (4 КБ), рисуется 48 px.
 */
export const PRO_SLOGAN = 'Каждый день приносит возможности. Узнай свои, пока они не прошли.';
export const OWNER_QUOTE = 'Я сама каждое утро смотрю свой прогноз — и не упускаю удачные дни. Хочу того же для тебя.';
export const OWNER_SIGNATURE = '— Светлана Филатова, астролог';
export const OWNER_PHOTO = '/img/svetlana-pro-192.webp';

// Все места вывода слогана на странице: {el, update}. Слоган у того, чей
// элемент раньше всех в документе.
const slots = new Set();

function firstSlot() {
  let first = null;
  slots.forEach((s) => {
    if (!s.el || !s.el.isConnected) return;
    // eslint-disable-next-line no-bitwise
    if (!first || (s.el.compareDocumentPosition(first.el) & Node.DOCUMENT_POSITION_FOLLOWING)) first = s;
  });
  return first;
}

function notifyAll() {
  const first = firstSlot();
  slots.forEach((s) => s.update(s === first));
}

function useSloganSlot(enabled) {
  const ref = useRef(null);
  const [first, setFirst] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    // Элемент читается из ref при каждом сравнении: у variant="slogan"
    // скрытая метка и показанный слоган — разные узлы.
    const slot = { get el() { return ref.current; }, update: setFirst };
    slots.add(slot);
    notifyAll();
    return () => {
      slots.delete(slot);
      notifyAll();
    };
  }, [enabled]);
  return [ref, enabled ? first : true];
}

export default function ProPitch({ variant = 'full', className = '', tone = 'light', standalone = false }) {
  const [slotRef, showSlogan] = useSloganSlot(!standalone);
  const onDark = tone === 'dark';
  if (variant === 'slogan' && !showSlogan) {
    return <span ref={slotRef} hidden data-testid="pro-slogan-slot" />;
  }
  const slogan = (
    <p
      className={`text-sm font-semibold leading-snug ${onDark ? 'text-white' : 'text-theme-800'}`}
      data-testid="pro-slogan"
    >
      {PRO_SLOGAN}
    </p>
  );
  if (variant === 'slogan') {
    return <div ref={slotRef} className={className}>{slogan}</div>;
  }
  return (
    <div ref={slotRef} className={`space-y-2 ${className}`} data-testid="pro-pitch">
      {showSlogan && slogan}
      <figure
        className={`flex items-start gap-3 rounded-xl p-2.5 ${onDark ? 'bg-white/15' : 'bg-theme-50 border border-theme-100'}`}
        data-testid="pro-owner"
      >
        <img
          src={OWNER_PHOTO}
          width="48"
          height="48"
          loading="lazy"
          alt="Светлана Филатова"
          className={`w-12 h-12 shrink-0 rounded-full object-cover border-2 ${onDark ? 'border-white/70' : 'border-theme-200'}`}
        />
        <div className="min-w-0">
          <blockquote className={`text-[13px] leading-snug italic ${onDark ? 'text-white' : 'text-slate-700'}`}>
            «{OWNER_QUOTE}»
          </blockquote>
          <figcaption className={`mt-1 text-xs font-semibold ${onDark ? 'text-white/85' : 'text-theme-700'}`}>
            {OWNER_SIGNATURE}
          </figcaption>
        </div>
      </figure>
    </div>
  );
}
